import { useFormikSubmit } from "@certn/api";
import { useClientPortalAPI } from "@certn/api/src/useClientPortalAPI";
import { showToast } from "@certn/cascadia";
import {
  BasicButton,
  Card,
  Container,
  Divider,
  PrimaryButton,
  Radio,
  RadioGroupFormikField,
  SectionMessage,
  SelectFormikField,
  SelectItem,
  Stack,
  Text,
  TextField,
  TextInputFormikField,
  ToggleField,
} from "@certn/cascadia-react";
import { css } from "@emotion/css";
import { Form, Formik, FormikHelpers } from "formik";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useCallback, useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import { object, string } from "yup";

import { APIError } from "@certn/api";
import { components } from "@certn/api/generated-schemas/client-portal";
import {
  GreenhouseIntegrationContext,
  AtsIntegration,
} from "../../GreenhouseIntegrationContext";
import { AtsIntegrationStepLayout } from "../AtsIntegrationStepLayout";
import { WhatsThis } from "../../../components/WhatsThis";

interface Props {
  initialValues?: components["schemas"]["AtsIntegrationDetail"];
  reload?: () => void;
  isEditing?: boolean;
}

export function GreenhouseSetupStep({
  initialValues,
  reload,
  isEditing,
}: Props = {}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { integrationsGreenhouseSentByAttribution } = useFlags();
  const [authenticationError, setAuthenticationError] = useState(false);
  const [isSystemError, setIsSystemError] = useState(false);
  const context = useContext(GreenhouseIntegrationContext);
  const { atsIntegrationData: contextData, setAtsIntegration } = context || {
    atsIntegrationData: null,
    setAtsIntegration: () => {},
  };

  // Determine if we're in edit mode
  const isEditMode = !!initialValues?.id;

  // Use initialValues for edit mode, context for create mode
  const integrationData = isEditMode
    ? {
        api_key: initialValues?.greenhouse_integration?.api_key || "",
        report_url_type: initialValues?.report_url_type || "",
        customer_group_id:
          initialValues?.greenhouse_integration?.customer_group_id || "",
        require_authorised_user:
          initialValues?.greenhouse_integration?.require_authorised_user ??
          true,
      }
    : contextData || {
        api_key: "",
        report_url_type: "CLIENT_PORTAL_URL",
        customer_group_id: "",
        require_authorised_user: true,
      };

  const { data: customerGroups, isLoading: isLoadingCustomerGroups } =
    useClientPortalAPI(
      "/api/internal/client-portal/ats-integrations/customer-groups/"
    );

  const validationSchema = object().shape({
    api_key: string().required().label(t("API key")),
    customer_group_id: string().required().label(t("Default group")),
  });

  const { submit: createIntegration, isSubmitting: isCreating } =
    useFormikSubmit("/api/internal/client-portal/ats-integrations/create/", {
      onSuccess: () => {
        showToast(
          t("Success, {{integration}} has been successfully enabled.", {
            integration: "Greenhouse",
          }),
          {
            type: "success",
          }
        );
        navigate("/settings/ats-integrations");
      },
      showToastOnError: true,
    });

  const { submit: updateIntegration, isSubmitting: isUpdating } =
    useFormikSubmit<components["schemas"]["AtsIntegrationDetail"]>(
      `/api/internal/client-portal/ats-integrations/${initialValues?.id}/update/`,
      {
        onSuccess: () => {
          showToast(t("Integration updated successfully"), {
            type: "success",
          });
          reload?.();
          navigate("/settings/ats-integrations");
        },
        showToastOnError: true,
      }
    );

  const handleSubmit = useCallback(
    async (
      values: AtsIntegration,
      formikHelpers: FormikHelpers<AtsIntegration>
    ) => {
      setAuthenticationError(false);
      setIsSystemError(false);
      if (!isEditMode) {
        setAtsIntegration(values);
      }

      try {
        if (isEditMode) {
          // Update mode - submit all values directly like other integrations
          await updateIntegration(
            {
              report_url_type: (values.report_url_type ||
                "CLIENT_PORTAL_URL") as components["schemas"]["ReportUrlTypeEnum"],
              lever_integration: null,
              workday_integration: null,
              greenhouse_integration: {
                api_key: values.api_key,
                customer_group_id: values.customer_group_id || "",
                require_authorised_user: values.require_authorised_user,
              },
            } as unknown as components["schemas"]["AtsIntegrationDetail"],
            formikHelpers
          );
        } else {
          // Create mode
          await createIntegration(
            {
              report_url_type: (values.report_url_type ||
                "CLIENT_PORTAL_URL") as components["schemas"]["ReportUrlTypeEnum"],
              greenhouse_integration: {
                api_key: values.api_key,
                customer_group_id: values.customer_group_id || "",
                require_authorised_user: values.require_authorised_user,
              },
            } as components["schemas"]["AtsIntegrationCreate"],
            formikHelpers
          );
        }
      } catch (error: unknown) {
        // Error handling is done in the onFailure callbacks
        // Check if it's a validation error or system error
        const status =
          error instanceof APIError
            ? error.status
            : (error as { status?: number; response?: { status?: number } })
                ?.status ||
              (error as { status?: number; response?: { status?: number } })
                ?.response?.status;
        if (status === 400 || status === 401 || status === 403) {
          setAuthenticationError(true);
          setIsSystemError(false);
        } else if (status !== undefined) {
          setIsSystemError(true);
          setAuthenticationError(false);
        }
      }
    },
    [isEditMode, setAtsIntegration, createIntegration, updateIntegration]
  );

  const isSubmitting = isCreating || isUpdating;
  const isInactive = initialValues?.is_active === false;

  return (
    <>
      <Formik
        initialValues={integrationData}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
        enableReinitialize={isEditMode}
      >
        {({ handleSubmit, isValid, values, ...helpers }) => {
          const copyToClipBoard = () => {
            navigator.clipboard.writeText(values?.api_key || "");
            showToast(t("URL copied to clipboard"), { type: "success" });
          };

          const formFields = (
            <>
              {authenticationError && (
                <SectionMessage
                  type="danger"
                  sectionTitle={t("Authentication failed")}
                  icon="error"
                >
                  <Trans>
                    We could not verify your API credentials. Please check your
                    details and try again. If you still encounter an error,
                    please contact your Greenhouse administrator.
                  </Trans>
                </SectionMessage>
              )}

              {isSystemError && (
                <SectionMessage
                  type="danger"
                  sectionTitle={t("System error")}
                  icon="error"
                >
                  <Trans>
                    We are not able to verify your API credentials, as the
                    Greenhouse platform is not responsive. Please try after some
                    time. If the issue persists, please contact Certn support
                    team.
                  </Trans>
                </SectionMessage>
              )}

              <Text style="global/heading-medium">
                {t("Greenhouse account authentication details")}
              </Text>

              <section>
                {isEditing ? (
                  <TextField
                    className={styles.textFieldSpacing}
                    label={t("API key")}
                    name="api_key"
                    testID="greenhouse-api-key"
                    isDisabled
                    isRequired
                    value={values.api_key}
                  />
                ) : (
                  <Stack orientation="horizontal" align="end" gap="space-2">
                    <TextInputFormikField
                      className={styles.textFieldSpacing}
                      name="api_key"
                      testID="greenhouse-api-key"
                      label={t("API key")}
                      isDisabled
                      isRequired
                      info={
                        <WhatsThis>
                          <div>
                            <Trans>
                              Your API key allows Certn to seamless integrate
                              with your Greenhouse account. You will be required
                              to send this to Greenhouse to enable your
                              integration. Please refer to the{" "}
                              <a
                                target="_blank"
                                href="https://help.certn.co/hc/en-us/articles/4402980872339-Certn-Greenhouse-Integration-Setup"
                                rel="noreferrer"
                              >
                                Greenhouse ↔ Certn Integration guide
                              </a>
                              .
                            </Trans>
                          </div>
                        </WhatsThis>
                      }
                    />
                    <BasicButton
                      onPress={copyToClipBoard}
                      type="button"
                      size="medium"
                      testID="copy-link-button"
                      leftIcon="link"
                    >
                      {t("Copy")}
                    </BasicButton>
                  </Stack>
                )}
              </section>

              <RadioGroupFormikField
                label={t("Report URL type")}
                testID="report-url-type"
                name="report_url_type"
                orientation="horizontal"
                isRequired
                isDisabled={isInactive}
                defaultValue="DIRECT_PDF_URL"
              >
                <Radio testID="direct-pdf-url" value="DIRECT_PDF_URL">
                  {t("PDF URL")}
                </Radio>
                <Radio testID="client-portal-url" value="CLIENT_PORTAL_URL">
                  {t("Client Portal URL (Authenticated)")}
                </Radio>
              </RadioGroupFormikField>

              {integrationsGreenhouseSentByAttribution && (
                <section>
                  <Stack orientation="horizontal" align="center" gap="space-2">
                    <ToggleField
                      label={t("Require Authorised Certn User")}
                      name="require_authorised_user"
                      testID="greenhouse-require-authorised-user"
                      aria-label={t("Require Authorised Certn User")}
                      isDisabled={isSubmitting || isInactive}
                    >
                      {t("Require Authorised Certn User")}
                    </ToggleField>
                    <div className={styles.noWrap}>
                      <WhatsThis>
                        <Text style="global/body-secondary" color="text-weak">
                          <Trans>
                            <strong>When enabled:</strong> Only Greenhouse users
                            with a matching Certn account (based on email
                            address) in the default group can initiate orders.
                            Orders are attributed to the corresponding Certn
                            user.
                          </Trans>
                        </Text>
                        <Text style="global/body-secondary" color="text-weak">
                          <Trans>
                            <strong>When disabled:</strong> Greenhouse users can
                            initiate orders without a matching Certn account. If
                            no matching account exists, orders are attributed to
                            the Certn user who configured the integration.
                          </Trans>
                        </Text>
                      </WhatsThis>
                    </div>
                  </Stack>
                </section>
              )}

              <Divider />

              <section>
                <SelectFormikField
                  label={t("Select a group")}
                  testID="greenhouse-customer-group"
                  name="customer_group_id"
                  isRequired
                  isLoading={isLoadingCustomerGroups}
                  isDisabled={isSubmitting || isInactive}
                >
                  {customerGroups?.map(({ id, name }) => (
                    <SelectItem key={id} id={id} textValue={name}>
                      {name}
                    </SelectItem>
                  ))}
                </SelectFormikField>
              </section>
            </>
          );

          const formContent = (
            <Container
              layout="narrow"
              testID="greenhouse-setup-page-outermost-container"
              className={styles.container}
            >
              <Form>
                {isEditMode && (
                  <Card type="large" className={styles.card}>
                    <Stack gap="space-4">
                      <div className={styles.mainContentContainer}>
                        {formFields}
                        <Stack
                          orientation="horizontal"
                          justify="end"
                          gap="space-4"
                        >
                          <BasicButton
                            testID="cancel-greenhouse"
                            type="button"
                            isDisabled={isSubmitting}
                            onPress={() =>
                              navigate("/settings/ats-integrations")
                            }
                          >
                            {t("Cancel")}
                          </BasicButton>
                          <PrimaryButton
                            testID="save-greenhouse"
                            type="submit"
                            isDisabled={
                              isSubmitting ||
                              !isValid ||
                              !initialValues?.is_active
                            }
                          >
                            {t("Save changes")}
                          </PrimaryButton>
                        </Stack>
                      </div>
                    </Stack>
                  </Card>
                )}

                {!isEditMode && (
                  <>
                    <Stack gap="space-2">
                      <Text
                        as="h1"
                        className={styles.titleSpacing}
                        style="global/title-small"
                      >
                        {t("Greenhouse Integration")}
                      </Text>
                      <Stack gap="space-2">
                        <Text style="global/heading-medium">
                          {t(
                            "Add your integration details here to set up the connection with Certn. These enable Certn to send information to Greenhouse."
                          )}
                        </Text>

                        <SectionMessage type="danger" icon="error">
                          <Trans>
                            Make sure you save the API key somewhere safe to
                            send to Greenhouse as you will not be able to see it
                            later.
                          </Trans>
                        </SectionMessage>
                      </Stack>
                    </Stack>
                    <div className={styles.mainContentContainer}>
                      {formFields}
                    </div>
                  </>
                )}
              </Form>
            </Container>
          );

          if (isEditMode) {
            return formContent;
          }

          return (
            <AtsIntegrationStepLayout
              onComplete={handleSubmit}
              disableCompletion={!isValid}
              submitting={isSubmitting}
            >
              {formContent}
            </AtsIntegrationStepLayout>
          );
        }}
      </Formik>
    </>
  );
}

const styles = {
  titleSpacing: css({ marginBottom: "var(--space-1)" }),
  mainContentContainer: css({
    display: "flex",
    gap: "var(--space-5)",
    flexDirection: "column",
    marginTop: "var(--space-4)",
  }),
  container: css({
    rowGap: "1rem !important",
  }),
  card: css({
    marginTop: "var(--space-4)",
    padding: "var(--space-4)",
    gap: "var(--space-4)",
  }),
  textFieldSpacing: css({ display: "flex", flex: "1" }),
  noWrap: css({ flexShrink: 0, whiteSpace: "nowrap" }),
};
